import { useState } from 'react';
import { Link } from 'react-router-dom';
import Testimonials from './components/Testimonials';

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Newsletter functionality will be added
    setEmail('');
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setShowMobileMenu(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/a5bacf18ca61cb357c27956f131c4424/4bda670db9801ed405ce2fad550ef8f6.png" 
                alt="Sierra Kustom Inc. Logo" 
                className="h-11 w-auto object-contain"
              />
              <span className="text-lg font-bold text-gray-900 tracking-tight">Sierra Kustom Inc.</span>
            </div>
            
            <div className="hidden md:flex items-center gap-7">
              <button onClick={() => scrollToSection('home')} className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">Home</button>
              <Link to="/services" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">Services</Link>
              <button onClick={() => scrollToSection('locations')} className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">Locations</button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('portfolio')} className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">Portfolio</button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
              <a href="tel:7805178242" className="bg-gradient-to-r from-amber-600 to-amber-500 text-white px-6 py-2.5 rounded-full hover:from-amber-700 hover:to-amber-600 transition-all cursor-pointer whitespace-nowrap font-semibold text-sm shadow-lg shadow-amber-600/30">
                780-517-8242
              </a>
            </div>

            <button 
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden text-gray-700 cursor-pointer"
            >
              <i className={`${showMobileMenu ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {showMobileMenu && (
            <div className="md:hidden mt-4 pb-4 flex flex-col gap-3">
              <button onClick={() => scrollToSection('home')} className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors cursor-pointer text-left whitespace-nowrap">Home</button>
              <Link to="/services" className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors cursor-pointer text-left whitespace-nowrap">Services</Link>
              <button onClick={() => scrollToSection('locations')} className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors cursor-pointer text-left whitespace-nowrap">Locations</button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors cursor-pointer text-left whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('portfolio')} className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors cursor-pointer text-left whitespace-nowrap">Portfolio</button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-medium text-gray-700 hover:text-amber-600 transition-colors cursor-pointer text-left whitespace-nowrap">Contact</button>
              <a href="tel:7805178242" className="bg-gradient-to-r from-amber-600 to-amber-500 text-white px-6 py-2.5 rounded-full hover:from-amber-700 hover:to-amber-600 transition-all cursor-pointer text-center whitespace-nowrap font-semibold text-sm">
                780-517-8242
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://public.readdy.ai/ai/img_res/4c7bce8267d8b6ea0adbe6ca623850b1.jpg"
            alt="Beautiful Interior Finishing"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/35 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-4xl">
            <div className="inline-block mb-6 px-5 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
              <span className="text-white/95 font-medium text-sm tracking-wide">Premium Interior Finishing & Cleaning</span>
            </div>
            
            <h1 className="text-7xl md:text-8xl font-bold text-white mb-6 leading-[0.95] tracking-tight">
              Sierra Kustom<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-200">Inc.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 mb-10 font-light leading-relaxed max-w-2xl">
              Transforming spaces across Alberta with expert craftsmanship in trim, flooring, decks, and professional cleaning services
            </p>
            
            <div className="flex flex-wrap items-center gap-3 mb-12">
              <div className="bg-white/15 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                <span className="text-white font-medium text-sm whitespace-nowrap">✓ Licensed & Insured</span>
              </div>
              <div className="bg-white/15 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                <span className="text-white font-medium text-sm whitespace-nowrap">✓ Expert Craftsmanship</span>
              </div>
              <div className="bg-white/15 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                <span className="text-white font-medium text-sm whitespace-nowrap">✓ 5-Star Service</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a 
                href="tel:7805178242"
                className="bg-white text-gray-900 px-8 py-4 rounded-full hover:bg-gray-50 transition-all cursor-pointer flex items-center gap-3 font-bold text-base whitespace-nowrap shadow-2xl shadow-black/30 group"
              >
                Get Free Quote
                <div className="w-9 h-9 bg-gradient-to-br from-amber-600 to-amber-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-phone-fill text-white text-base"></i>
                </div>
              </a>
              <a href="tel:7805178242" className="text-white text-lg font-semibold hover:text-amber-300 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
                <i className="ri-phone-line"></i>780-517-8242
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gradient-to-b from-stone-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <div className="inline-block mb-4 px-5 py-2 bg-amber-100 rounded-full">
              <span className="text-amber-700 font-semibold text-sm tracking-wide uppercase">Our Services</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-5 tracking-tight">
              Expert <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-amber-500">Craftsmanship</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Professional services for residential and commercial spaces across Alberta
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="group relative overflow-hidden rounded-3xl shadow-md hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] cursor-pointer bg-white border border-gray-100">
              <div className="h-72 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20carpenter%20installing%20white%20trim%20and%20door%20frame%20in%20modern%20home%20interior%2C%20detailed%20woodwork%20craftsmanship%2C%20clean%20precise%20cuts%2C%20elegant%20door%20installation%20with%20beautiful%20molding%20and%20baseboards%2C%20warm%20wood%20tones%20against%20white%20walls%2C%20residential%20construction%20photography&width=800&height=600&seq=service-trim-001&orientation=landscape"
                  alt="Trim & Door Installation"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="p-8 bg-white">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-door-line text-amber-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Trim & Door Installation</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Precision trim work and professional door installation that adds elegance and value to your property.
                </p>
                <Link to="/services#trim-doors" className="inline-flex items-center gap-2 mt-4 text-amber-600 font-semibold text-sm hover:text-amber-700 transition-colors whitespace-nowrap group/link">
                  View Full Details
                  <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-3xl shadow-md hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] cursor-pointer bg-white border border-gray-100">
              <div className="h-72 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Beautiful%20premium%20hardwood%20flooring%20installation%20in%20modern%20home%2C%20rich%20wood%20grain%20texture%2C%20elegant%20oak%20or%20maple%20planks%2C%20professional%20floor%20laying%20craftsmanship%2C%20warm%20natural%20wood%20tones%2C%20residential%20interior%20flooring%20photography%20with%20natural%20lighting&width=800&height=600&seq=service-floor-001&orientation=landscape"
                  alt="Premium Flooring"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="p-8 bg-white">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-layout-grid-line text-amber-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Premium Flooring</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  High-quality flooring installation including hardwood, laminate, and vinyl for lasting beauty.
                </p>
                <Link to="/services#flooring" className="inline-flex items-center gap-2 mt-4 text-amber-600 font-semibold text-sm hover:text-amber-700 transition-colors whitespace-nowrap group/link">
                  View Full Details
                  <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="group relative overflow-hidden rounded-3xl shadow-md hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] cursor-pointer bg-white border border-gray-100">
              <div className="h-72 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20window%20installation%20in%20modern%20home%2C%20skilled%20installer%20fitting%20new%20energy%20efficient%20vinyl%20or%20wood%20frame%20window%2C%20clean%20precise%20installation%20work%2C%20residential%20window%20replacement%20service%2C%20bright%20natural%20light%20streaming%20through%20new%20windows%2C%20construction%20photography&width=800&height=600&seq=service-window-001&orientation=landscape"
                  alt="Window Installation"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="p-8 bg-white">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-window-line text-amber-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Window Installation</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Expert window installation and replacement services for improved energy efficiency and curb appeal.
                </p>
                <Link to="/services#windows" className="inline-flex items-center gap-2 mt-4 text-amber-600 font-semibold text-sm hover:text-amber-700 transition-colors whitespace-nowrap group/link">
                  View Full Details
                  <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-3xl shadow-md hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] cursor-pointer bg-white border border-gray-100">
              <div className="h-72 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Beautiful%20custom%20wooden%20fence%20construction%20in%20residential%20backyard%2C%20professional%20fence%20building%20craftsmanship%2C%20natural%20wood%20grain%20cedar%20or%20pine%20fencing%2C%20clean%20precise%20installation%20with%20posts%20and%20panels%2C%20modern%20residential%20property%20fencing%2C%20outdoor%20construction%20photography&width=800&height=600&seq=service-fence-001&orientation=landscape"
                  alt="Fencing"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="p-8 bg-white">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-layout-row-line text-amber-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Fencing</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Quality fence installation for privacy, security, and aesthetic enhancement of your property.
                </p>
                <Link to="/services#fencing" className="inline-flex items-center gap-2 mt-4 text-amber-600 font-semibold text-sm hover:text-amber-700 transition-colors whitespace-nowrap group/link">
                  View Full Details
                  <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="group relative overflow-hidden rounded-3xl shadow-md hover:shadow-2xl transition-all duration-500 hover:scale-[1.01] cursor-pointer bg-white border border-gray-100">
              <div className="h-64 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Stunning%20custom%20wooden%20deck%20construction%20in%20backyard%2C%20beautiful%20outdoor%20living%20space%20with%20premium%20wood%20decking%2C%20professional%20deck%20building%20craftsmanship%2C%20natural%20wood%20grain%20texture%2C%20modern%20residential%20deck%20design%20with%20railings%2C%20warm%20cedar%20or%20composite%20materials&width=1400&height=500&seq=service-deck-001&orientation=landscape"
                  alt="Custom Deck Building"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="p-8 bg-white">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-home-4-line text-amber-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Custom Deck Building</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Transform your outdoor space with expertly crafted custom decks built to withstand Alberta's climate.
                </p>
                <Link to="/services#decks" className="inline-flex items-center gap-2 mt-4 text-amber-600 font-semibold text-sm hover:text-amber-700 transition-colors whitespace-nowrap group/link">
                  View Full Details
                  <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="md:col-span-2 group relative overflow-hidden rounded-3xl shadow-md hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] cursor-pointer bg-white border border-gray-100">
              <div className="h-72 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Sparkling%20clean%20modern%20residential%20home%20interior%2C%20bright%20and%20fresh%20living%20room%20with%20pristine%20white%20surfaces%2C%20professional%20house%20cleaning%20service%20results%2C%20immaculate%20home%20environment%2C%20natural%20sunlight%20streaming%20through%20clean%20windows%2C%20organized%20and%20tidy%20residential%20space&width=600&height=600&seq=service-resi-001&orientation=squarish"
                  alt="Residential Cleaning"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="p-8 bg-white">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-home-smile-line text-amber-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Residential Cleaning</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Thorough home cleaning services to keep your living space spotless and comfortable.
                </p>
                <Link to="/services#residential-cleaning" className="inline-flex items-center gap-2 mt-4 text-amber-600 font-semibold text-sm hover:text-amber-700 transition-colors whitespace-nowrap group/link">
                  View Full Details
                  <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>

            <div className="md:col-span-3 group relative overflow-hidden rounded-3xl shadow-md hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] cursor-pointer bg-white border border-gray-100">
              <div className="h-72 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20office%20cleaning%20service%20in%20modern%20corporate%20workspace%2C%20clean%20and%20organized%20business%20environment%2C%20spotless%20office%20interior%20with%20desks%20and%20computers%2C%20commercial%20cleaning%20results%2C%20bright%20professional%20office%20space%20with%20clean%20floors%20and%20windows&width=900&height=600&seq=service-office-001&orientation=landscape"
                  alt="Office Cleaning Services"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="p-8 bg-white">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-building-line text-amber-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Office Cleaning Services</h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  Professional commercial cleaning to maintain a pristine and productive work environment for your business.
                </p>
                <Link to="/services#office-cleaning" className="inline-flex items-center gap-2 mt-4 text-amber-600 font-semibold text-sm hover:text-amber-700 transition-colors whitespace-nowrap group/link">
                  View Full Details
                  <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* View All Services CTA */}
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white px-10 py-4 rounded-full hover:from-amber-700 hover:to-amber-600 transition-all cursor-pointer font-bold text-base whitespace-nowrap shadow-lg shadow-amber-600/30 group"
            >
              Explore All Services
              <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section id="locations" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-amber-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-sky-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block mb-4 px-5 py-2 bg-sky-500/20 backdrop-blur-sm rounded-full border border-sky-400/30">
                <span className="text-sky-300 font-semibold text-sm tracking-wide uppercase">Serving Alberta</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                Proudly Serving<br />Multiple Communities
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                From Edmonton to Whitecourt, we bring professional interior finishing and cleaning services to your doorstep across Alberta.
              </p>
              <button 
                onClick={() => scrollToSection('contact')}
                className="border-2 border-white text-white px-8 py-3.5 rounded-full hover:bg-white hover:text-gray-900 transition-all cursor-pointer font-semibold flex items-center gap-2 whitespace-nowrap text-sm group"
              >
                View Service Areas
                <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i>
              </button>
            </div>

            <div className="relative h-96">
              <div className="absolute top-0 left-12 bg-gradient-to-br from-sky-500/30 to-sky-600/20 backdrop-blur-md px-7 py-5 rounded-2xl border border-sky-400/40 transform -rotate-2 hover:rotate-0 transition-all duration-300 cursor-pointer shadow-xl">
                <h3 className="text-xl font-bold text-white mb-1">Edmonton</h3>
                <p className="text-sky-200 text-sm">Alberta</p>
                <span className="text-sky-300 text-xs mt-1 block">Get a Quote →</span>
              </div>

              <div className="absolute top-20 right-8 bg-gradient-to-br from-amber-500/30 to-amber-600/20 backdrop-blur-md px-7 py-5 rounded-2xl border border-amber-400/40 transform rotate-3 hover:rotate-0 transition-all duration-300 cursor-pointer shadow-xl">
                <h3 className="text-xl font-bold text-white mb-1">Edson</h3>
                <p className="text-amber-200 text-sm">Alberta</p>
                <span className="text-amber-300 text-xs mt-1 block">Get a Quote →</span>
              </div>

              <div className="absolute top-40 left-24 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 backdrop-blur-md px-7 py-5 rounded-2xl border border-emerald-400/40 transform -rotate-1 hover:rotate-0 transition-all duration-300 cursor-pointer shadow-xl">
                <h3 className="text-xl font-bold text-white mb-1">Hinton</h3>
                <p className="text-emerald-200 text-sm">Alberta</p>
                <span className="text-emerald-300 text-xs mt-1 block">Get a Quote →</span>
              </div>

              <div className="absolute bottom-20 right-16 bg-gradient-to-br from-sky-500/30 to-sky-600/20 backdrop-blur-md px-7 py-5 rounded-2xl border border-sky-400/40 transform rotate-2 hover:rotate-0 transition-all duration-300 cursor-pointer shadow-xl">
                <h3 className="text-xl font-bold text-white mb-1">Whitecourt</h3>
                <p className="text-sky-200 text-sm">Alberta</p>
                <span className="text-sky-300 text-xs mt-1 block">Get a Quote →</span>
              </div>

              <div className="absolute bottom-2 left-8 bg-gradient-to-br from-amber-500/30 to-amber-600/20 backdrop-blur-md px-7 py-5 rounded-2xl border border-amber-400/40 transform -rotate-3 hover:rotate-0 transition-all duration-300 cursor-pointer shadow-xl">
                <h3 className="text-xl font-bold text-white mb-1">Spruce Grove</h3>
                <p className="text-amber-200 text-sm">Alberta</p>
                <span className="text-amber-300 text-xs mt-1 block">Get a Quote →</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <div className="inline-block mb-6 px-5 py-2 bg-emerald-100 rounded-full">
              <span className="text-emerald-700 font-semibold text-sm tracking-wide uppercase">About Sierra Kustom</span>
            </div>
            
            <div className="mb-12">
              <h2 className="text-6xl md:text-7xl font-bold text-gray-900 leading-[1.1] mb-8 tracking-tight">
                Crafting Quality<br />
                Interiors with<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-700 to-emerald-500">Precision & Care</span>
              </h2>
            </div>

            <div className="space-y-6 mb-12">
              <p className="text-lg text-gray-700 leading-relaxed">
                Sierra Kustom Inc. is your trusted partner for premium interior finishing and professional cleaning services across Alberta. With years of experience and a commitment to excellence, we transform residential and commercial spaces into beautiful, functional environments.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our skilled team specializes in trim and door installation, premium flooring, custom deck building, and comprehensive cleaning services. We take pride in our attention to detail, quality craftsmanship, and dedication to customer satisfaction.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you're renovating your home, building a new deck, or need regular cleaning services, Sierra Kustom Inc. delivers exceptional results that exceed expectations. We serve communities throughout Alberta, bringing professional expertise right to your doorstep.
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-emerald-700 to-emerald-600 text-white px-8 py-3.5 rounded-full hover:from-emerald-800 hover:to-emerald-700 transition-all cursor-pointer font-semibold text-sm whitespace-nowrap shadow-lg shadow-emerald-700/30"
              >
                Schedule Consultation
              </button>
              <a 
                href="tel:7805178242"
                className="border-2 border-emerald-700 text-emerald-700 px-8 py-3.5 rounded-full hover:bg-emerald-700 hover:text-white transition-all cursor-pointer font-semibold text-sm whitespace-nowrap"
              >
                Call 780-517-8242
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-24 bg-gradient-to-b from-stone-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <div className="inline-block mb-4 px-5 py-2 bg-amber-100 rounded-full">
              <span className="text-amber-700 font-semibold text-sm tracking-wide uppercase">Portfolio</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-5 tracking-tight">
              Our Recent <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-amber-500">Projects</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Take a look at some of our completed work showcasing quality craftsmanship
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div className="h-80 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Completed%20residential%20interior%20trim%20work%20project%20showing%20elegant%20white%20crown%20molding%2C%20baseboards%2C%20and%20door%20frames%20in%20modern%20home%2C%20professional%20carpentry%20craftsmanship%2C%20clean%20precise%20installation%2C%20beautiful%20finished%20interior%20woodwork%20with%20warm%20lighting&width=600&height=800&seq=portfolio-trim-001&orientation=portrait"
                  alt="Trim Installation Project"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <h3 className="text-lg font-bold text-white mb-1">Custom Trim Installation</h3>
                <p className="text-white/80 text-sm mb-3">Edmonton Residence</p>
                <Link to="/services#trim-doors" className="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 text-xs font-semibold transition-colors whitespace-nowrap">
                  View Trim & Door Service <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div className="h-80 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Completed%20hardwood%20flooring%20installation%20project%20in%20modern%20home%2C%20beautiful%20oak%20or%20maple%20wood%20floor%20with%20rich%20grain%20patterns%2C%20professional%20floor%20laying%20results%2C%20warm%20natural%20wood%20tones%2C%20residential%20interior%20with%20new%20premium%20flooring&width=600&height=800&seq=portfolio-floor-001&orientation=portrait"
                  alt="Flooring Project"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <h3 className="text-lg font-bold text-white mb-1">Hardwood Flooring</h3>
                <p className="text-white/80 text-sm mb-3">Spruce Grove Home</p>
                <Link to="/services#flooring" className="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 text-xs font-semibold transition-colors whitespace-nowrap">
                  View Flooring Service <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div className="h-80 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Completed%20custom%20deck%20building%20project%20in%20backyard%2C%20beautiful%20wooden%20deck%20with%20railings%20and%20stairs%2C%20professional%20deck%20construction%20results%2C%20natural%20cedar%20or%20composite%20materials%2C%20outdoor%20living%20space%20with%20furniture%2C%20Alberta%20residential%20property&width=600&height=800&seq=portfolio-deck-001&orientation=portrait"
                  alt="Deck Building Project"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <h3 className="text-lg font-bold text-white mb-1">Custom Deck Build</h3>
                <p className="text-white/80 text-sm mb-3">Hinton Property</p>
                <Link to="/services#decks" className="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 text-xs font-semibold transition-colors whitespace-nowrap">
                  View Deck Building Service <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div className="h-96 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Before%20and%20after%20residential%20cleaning%20service%20results%2C%20sparkling%20clean%20modern%20home%20interior%2C%20pristine%20living%20room%20with%20clean%20surfaces%20and%20organized%20space%2C%20professional%20house%20cleaning%20transformation%2C%20bright%20and%20fresh%20residential%20environment%20with%20natural%20light&width=800&height=600&seq=portfolio-clean-001&orientation=landscape"
                  alt="Residential Cleaning"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <h3 className="text-lg font-bold text-white mb-1">Residential Deep Clean</h3>
                <p className="text-white/80 text-sm mb-3">Edson Family Home</p>
                <Link to="/services#residential-cleaning" className="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 text-xs font-semibold transition-colors whitespace-nowrap">
                  View Residential Cleaning <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div className="h-96 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20office%20cleaning%20service%20results%20in%20modern%20corporate%20workspace%2C%20spotless%20business%20environment%20with%20clean%20desks%20and%20organized%20space%2C%20commercial%20cleaning%20transformation%2C%20bright%20professional%20office%20interior%20with%20clean%20floors%20and%20windows&width=800&height=600&seq=portfolio-office-001&orientation=landscape"
                  alt="Office Cleaning"
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <h3 className="text-lg font-bold text-white mb-1">Commercial Office Cleaning</h3>
                <p className="text-white/80 text-sm mb-3">Whitecourt Business Center</p>
                <Link to="/services#office-cleaning" className="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 text-xs font-semibold transition-colors whitespace-nowrap">
                  View Office Cleaning Service <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Portfolio CTA */}
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 border-2 border-amber-500 text-amber-600 px-8 py-3.5 rounded-full hover:bg-amber-500 hover:text-white transition-all cursor-pointer font-semibold text-sm whitespace-nowrap"
            >
              See All Our Services
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Window & Fencing Gallery */}
      <section id="gallery" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <div className="inline-block mb-4 px-5 py-2 bg-emerald-100 rounded-full">
              <span className="text-emerald-700 font-semibold text-sm tracking-wide uppercase">Gallery</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-5 tracking-tight">
              Window & Fencing <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-700 to-emerald-500">Gallery</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Browse our latest window installation and fencing projects across Alberta communities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer md:col-span-2">
              <div className="h-96 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Stunning%20large%20bay%20window%20installation%20in%20modern%20residential%20home%20exterior%2C%20brand%20new%20energy%20efficient%20triple%20pane%20windows%20with%20white%20vinyl%20frames%2C%20professional%20window%20replacement%20project%20completed%2C%20beautiful%20curb%20appeal%20with%20natural%20light%20streaming%20through%2C%20Alberta%20residential%20property%20photography&width=1000&height=700&seq=gallery-window-001&orientation=landscape"
                  alt="Bay Window Installation Edmonton"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Window</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-1">Bay Window Replacement</h3>
                <p className="text-white/80 text-sm">Edmonton — Energy-efficient triple-pane upgrade</p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div className="h-96 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Beautiful%20tall%20cedar%20privacy%20fence%20newly%20installed%20in%20residential%20backyard%2C%20professional%20fence%20construction%20with%20clean%20vertical%20boards%20and%20sturdy%20posts%2C%20natural%20wood%20grain%20texture%2C%20green%20lawn%20and%20garden%20visible%2C%20Alberta%20residential%20property%20outdoor%20photography%20with%20clear%20sky&width=600&height=800&seq=gallery-fence-001&orientation=portrait"
                  alt="Cedar Privacy Fence Spruce Grove"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Fencing</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-1">Cedar Privacy Fence</h3>
                <p className="text-white/80 text-sm">Spruce Grove — Full backyard enclosure</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div className="h-80 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20horizontal%20slat%20wood%20fence%20with%20metal%20posts%20in%20contemporary%20residential%20yard%2C%20stylish%20modern%20fence%20design%20with%20clean%20lines%2C%20warm%20stained%20wood%20planks%2C%20professional%20fence%20building%20craftsmanship%2C%20landscaped%20garden%20visible%20behind%20fence%2C%20outdoor%20construction%20photography&width=600&height=700&seq=gallery-fence-002&orientation=portrait"
                  alt="Modern Horizontal Fence Edson"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Fencing</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-1">Modern Horizontal Fence</h3>
                <p className="text-white/80 text-sm">Edson — Contemporary design</p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div className="h-80 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=New%20casement%20windows%20installed%20in%20residential%20home%20bedroom%2C%20bright%20natural%20light%20flooding%20through%20clean%20glass%20panes%2C%20white%20vinyl%20window%20frames%20with%20modern%20hardware%2C%20professional%20window%20installation%20results%2C%20cozy%20interior%20room%20with%20curtains%20pulled%20back%20showing%20new%20windows&width=600&height=700&seq=gallery-window-002&orientation=portrait"
                  alt="Casement Window Installation Hinton"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Window</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-1">Casement Window Upgrade</h3>
                <p className="text-white/80 text-sm">Hinton — Bedroom renovation</p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div className="h-80 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Decorative%20wooden%20picket%20fence%20with%20gate%20entrance%20in%20front%20yard%20of%20charming%20residential%20home%2C%20white%20painted%20picket%20fence%20with%20arched%20gate%2C%20professional%20fence%20installation%20craftsmanship%2C%20green%20lawn%20and%20flower%20garden%2C%20welcoming%20curb%20appeal%2C%20Alberta%20residential%20property%20photography&width=600&height=700&seq=gallery-fence-003&orientation=portrait"
                  alt="Picket Fence with Gate Whitecourt"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Fencing</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-1">Picket Fence &amp; Gate</h3>
                <p className="text-white/80 text-sm">Whitecourt — Front yard charm</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div className="h-80 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Large%20picture%20window%20installation%20in%20modern%20living%20room%20with%20panoramic%20view%2C%20floor%20to%20ceiling%20glass%20window%20with%20slim%20black%20aluminum%20frame%2C%20professional%20window%20replacement%20showing%20stunning%20outdoor%20view%2C%20bright%20airy%20interior%20space%20with%20natural%20light%2C%20contemporary%20residential%20architecture&width=800&height=600&seq=gallery-window-003&orientation=landscape"
                  alt="Picture Window Installation Edmonton"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Window</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-1">Picture Window Installation</h3>
                <p className="text-white/80 text-sm">Edmonton — Living room panoramic view</p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer">
              <div className="h-80 w-full overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Long%20stretch%20of%20professional%20chain%20link%20fence%20with%20privacy%20slats%20installed%20around%20commercial%20property%2C%20sturdy%20galvanized%20steel%20fence%20with%20green%20privacy%20inserts%2C%20clean%20professional%20fence%20installation%20along%20property%20line%2C%20commercial%20fencing%20project%20completed%2C%20outdoor%20construction%20photography&width=800&height=600&seq=gallery-fence-004&orientation=landscape"
                  alt="Commercial Fencing Project"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Fencing</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-1">Commercial Property Fencing</h3>
                <p className="text-white/80 text-sm">Edson — Full perimeter security fence</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={() => scrollToSection('contact')}
              className="bg-gradient-to-r from-emerald-700 to-emerald-600 text-white px-10 py-4 rounded-full hover:from-emerald-800 hover:to-emerald-700 transition-all cursor-pointer font-bold text-base whitespace-nowrap shadow-lg shadow-emerald-700/30 inline-flex items-center gap-3 group"
            >
              Get a Free Quote
              <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <Testimonials />
      <section id="contact" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://readdy.ai/api/search-image?query=Beautiful%20completed%20interior%20finishing%20project%20showing%20elegant%20modern%20home%20with%20pristine%20trim%20work%2C%20hardwood%20floors%2C%20and%20custom%20details%2C%20professional%20residential%20construction%20results%2C%20warm%20inviting%20interior%20space%20with%20natural%20light%2C%20high-end%20craftsmanship%20photography&width=1920&height=800&seq=cta-bg-001&orientation=landscape"
            alt="Contact Background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/70"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight leading-tight">
            Ready to Transform<br />Your Space?
          </h2>
          <p className="text-xl text-white/90 mb-10 font-light leading-relaxed">
            Contact us today for a free consultation and quote
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            <a 
              href="tel:7805178242"
              className="bg-white text-gray-900 px-10 py-4 rounded-full hover:bg-gray-50 transition-all cursor-pointer font-bold text-base flex items-center gap-3 whitespace-nowrap shadow-2xl group"
            >
              <i className="ri-phone-fill text-xl group-hover:scale-110 transition-transform"></i>
              780-517-8242
            </a>
            <a 
              href="#services"
              className="bg-amber-500 text-white px-10 py-4 rounded-full hover:bg-amber-600 transition-all cursor-pointer font-bold text-base flex items-center gap-3 whitespace-nowrap shadow-2xl"
            >
              View Our Services
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>

          {/* Contact Form */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 md:p-12 shadow-2xl max-w-2xl mx-auto">
            <h3 className="text-3xl font-bold text-gray-900 mb-2">Send Us a Message</h3>
            <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you within 24 hours</p>
            
            <form 
              id="contact-form"
              data-readdy-form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
                const statusMessage = form.querySelector('#form-status') as HTMLDivElement;
                
                try {
                  submitButton.disabled = true;
                  submitButton.innerHTML = '<i class="ri-loader-4-line animate-spin text-xl"></i> Sending...';
                  
                  const formData = new FormData(form);
                  const urlEncodedData = new URLSearchParams();
                  
                  formData.forEach((value, key) => {
                    urlEncodedData.append(key, value.toString());
                  });
                  
                  const response = await fetch('https://readdy.ai/api/form/d5m6hdolhqshf1uh0cm0', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: urlEncodedData.toString()
                  });
                  
                  if (response.ok) {
                    statusMessage.className = 'mt-4 p-4 bg-green-100 text-green-800 rounded-lg text-sm font-medium';
                    statusMessage.textContent = '✓ Message sent successfully! We\'ll contact you soon.';
                    form.reset();
                  } else {
                    throw new Error('Submission failed');
                  }
                } catch (error) {
                  statusMessage.className = 'mt-4 p-4 bg-red-100 text-red-800 rounded-lg text-sm font-medium';
                  statusMessage.textContent = '✗ Failed to send message. Please try calling us directly.';
                } finally {
                  submitButton.disabled = false;
                  submitButton.innerHTML = 'Send Message <i class="ri-send-plane-fill text-lg ml-2"></i>';
                }
              }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2 text-left">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-full pr-12 focus:outline-none focus:border-amber-500 transition-colors text-sm"
                    placeholder="John Smith"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2 text-left">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-full pr-12 focus:outline-none focus:border-amber-500 transition-colors text-sm"
                    placeholder="(780) 123-4567"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2 text-left">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-full pr-12 focus:outline-none focus:border-amber-500 transition-colors text-sm"
                  placeholder="john@example.com"
                />
              </div>
              
              <div>
                <label htmlFor="service" className="block text-sm font-bold text-gray-700 mb-2 text-left">
                  Service Interested In *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-full pr-12 focus:outline-none focus:border-amber-500 transition-colors text-sm cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="Trim Installation">Trim Installation</option>
                  <option value="Door Installation">Door Installation</option>
                  <option value="Flooring">Flooring</option>
                  <option value="Window Installation">Window Installation</option>
                  <option value="Fencing">Fencing</option>
                  <option value="Deck Construction">Deck Construction</option>
                  <option value="Residential Cleaning">Residential Cleaning</option>
                  <option value="Office Cleaning">Office Cleaning</option>
                  <option value="Multiple Services">Multiple Services</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-bold text-gray-700 mb-2 text-left">
                  Your Location *
                </label>
                <select
                  id="location"
                  name="location"
                  required
                  className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-full pr-12 focus:outline-none focus:border-amber-500 transition-colors text-sm cursor-pointer"
                >
                  <option value="">Select your location</option>
                  <option value="Edmonton">Edmonton</option>
                  <option value="Edson">Edson</option>
                  <option value="Hinton">Hinton</option>
                  <option value="Whitecourt">Whitecourt</option>
                  <option value="Spruce Grove">Spruce Grove</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-2 text-left">
                  Project Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-full pr-12 focus:outline-none focus:border-amber-500 transition-colors text-sm resize-none"
                  placeholder="Tell us about your project... (max 500 characters)"
                ></textarea>
              </div>
              
              <button
                type="submit"
                className="w-full bg-amber-500 text-white px-8 py-4 rounded-full hover:bg-amber-600 transition-all cursor-pointer font-bold text-base flex items-center justify-center gap-2 whitespace-nowrap shadow-lg"
              >
                Send Message
                <i className="ri-send-plane-fill text-lg"></i>
              </button>
              
              <div id="form-status"></div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 mx-6 mb-6 rounded-3xl relative overflow-hidden shadow-2xl">
        <div className="absolute bottom-0 left-0 right-0 text-slate-800 text-9xl font-bold opacity-5 text-center pb-8 select-none pointer-events-none">
          SIERRA KUSTOM
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/a5bacf18ca61cb357c27956f131c4424/4bda670db9801ed405ce2fad550ef8f6.png" 
                  alt="Sierra Kustom Inc." 
                  className="h-10 w-auto object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Sierra Kustom Inc.</h3>
              <p className="text-gray-400 leading-relaxed text-sm">
                Premium interior finishing and cleaning services across Alberta.
              </p>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-4">Services</h4>
              <ul className="space-y-3">
                <li><Link to="/services#trim-doors" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Trim &amp; Doors</Link></li>
                <li><Link to="/services#flooring" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Flooring</Link></li>
                <li><Link to="/services#windows" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Windows</Link></li>
                <li><Link to="/services#fencing" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Fencing</Link></li>
                <li><Link to="/services#decks" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Decks</Link></li>
                <li><Link to="/services#residential-cleaning" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Residential Cleaning</Link></li>
                <li><Link to="/services#office-cleaning" className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm">Office Cleaning</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-4">Service Areas</h4>
              <ul className="space-y-3">
                <li className="text-gray-400 flex items-center gap-2 text-sm">
                  <i className="ri-map-pin-line text-amber-500"></i>
                  Edmonton
                </li>
                <li className="text-gray-400 flex items-center gap-2 text-sm">
                  <i className="ri-map-pin-line text-amber-500"></i>
                  Edson
                </li>
                <li className="text-gray-400 flex items-center gap-2 text-sm">
                  <i className="ri-map-pin-line text-amber-500"></i>
                  Hinton
                </li>
                <li className="text-gray-400 flex items-center gap-2 text-sm">
                  <i className="ri-map-pin-line text-amber-500"></i>
                  Whitecourt
                </li>
                <li className="text-gray-400 flex items-center gap-2 text-sm">
                  <i className="ri-map-pin-line text-amber-500"></i>
                  Spruce Grove
                </li>
                <li className="text-gray-400 flex items-center gap-2 text-sm">
                  <i className="ri-map-pin-line text-amber-500"></i>
                  Mexico
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-base font-semibold text-white mb-4">Get In Touch</h4>
              <a href="tel:7805178242" className="text-xl font-bold text-white hover:text-amber-500 transition-colors cursor-pointer flex items-center gap-2 mb-3 whitespace-nowrap">
                <i className="ri-phone-fill"></i>
                780-517-8242
              </a>
              <a href="mailto:Info@sierrakustoms.ca" className="text-base text-gray-300 hover:text-amber-500 transition-colors cursor-pointer flex items-center gap-2 mb-6 whitespace-nowrap">
                <i className="ri-mail-fill"></i>
                Info@sierrakustoms.ca
              </a>
              <form onSubmit={handleNewsletterSubmit} className="relative">
                <input 
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-full pr-12 focus:outline-none focus:border-amber-500 transition-colors text-sm"
                  required
                />
                <button 
                  type="submit"
                  className="absolute right-1 top-1 w-10 h-10 bg-gradient-to-r from-amber-600 to-amber-500 rounded-full flex items-center justify-center hover:from-amber-700 hover:to-amber-600 transition-all cursor-pointer shadow-lg"
                >
                  <i className="ri-arrow-right-line text-white text-base"></i>
                </button>
              </form>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © 2024 Sierra Kustom Inc. All rights reserved.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
